/**
 * The two document operations that change the SHAPE of the tree: delete and
 * move. Beside `documents.js` rather than inside it, because they fail in a way
 * nothing else in that file can: they can make rows unreachable.
 *
 * ## Every delete here is a TOMBSTONE, and there is no undo
 *
 * `extension_records_delete` sets `deleted = 1`, bumps the version and queues
 * one outbox `delete` op. Sync carries that tombstone to every other device the
 * user owns and, in a team scope, to every teammate — their copy disappears
 * too. The row physically survives in SQLite, so a restore is theoretically an
 * upsert of the same id, but **no tool, no command and no UI does that**, and
 * the app states its own position at `deletedDocsGuard.js:24-25`: *"there is no
 * undelete (tombstones are permanent from the client's point of view)"*.
 *
 * So nothing here may imply an undo, and the tool descriptions say so.
 *
 * ## The subtree contract, which is not new
 *
 * `DocumentsSidebar.js:737-739`, verbatim: *"Deleting any node deletes its whole
 * subtree — documents can hold subpages now, so a document delete must cascade
 * exactly like a folder delete or it orphans the children (rows still in the
 * store, unreachable from the root)."* That contract is extracted into
 * `documents/documentsTree.js` and shared with the sidebar, so there is one
 * walk rather than two.
 *
 * ## `cascade: true` is an argument, not a confirmation
 *
 * Above one node a delete is refused unless the caller passes `cascade: true`,
 * and the refusal LISTS what would have been deleted. The user's confirmation
 * is the destructive switch on the settings page — that decision is made and is
 * not re-litigated here. This is a different thing: it makes a model state its
 * intent to remove a tree, and it mirrors the app's own threshold exactly
 * (`DocumentsSidebar.js:759-763` deletes a childless node with no prompt and
 * raises a ConfirmModal otherwise).
 *
 * ## Children before the parent
 *
 * The subtree is deleted deepest-first. A failure part-way then leaves an
 * over-full parent — visible in the sidebar, and deletable again. The other
 * order leaves orphans under a deleted parent: still in the store, still
 * syncing, still counting against the per-scope row cap, and unreachable from
 * any UI forever. That is the exact failure the sidebar's comment names.
 *
 * ## The open tab is NOT closed, on purpose
 *
 * The sidebar closes a deleted document's tabs first, through `closeDocumentTab`
 * — which lives in `tabsStore`, a module `src/mcp/` is forbidden from naming.
 * MCP does not need it: `deleteNodes` marks every document id in
 * `deletedDocsGuard` synchronously before its first write, so a late autosave
 * flush from that tab is a no-op and cannot resurrect the row. The residue is a
 * stale tab showing a document that no longer exists; the user closes it. That
 * is a cosmetic cost, and the alternative is widening a security guard's
 * expected value for it.
 */
import { listTree, deleteNodes } from '../../extensions/bundled/documents/documentsStore';
import { indexNodes, collectSubtree } from '../../extensions/bundled/documents/documentsTree';
import { toDocScope, PERSONAL } from '../scopes';
import { mcpError } from '../errors';
import { refuseUnwritableDocScope, docScopeReport as scopeReport } from '../docScopes';

/** `[{id,type,name}]` — what a `cascade_required` refusal shows the model. */
const summarise = (nodes) =>
  nodes.map((n) => ({ id: n.id, type: n.type, name: n.name || null }));

/**
 * Delete a node and everything beneath it. Destructive; no undo.
 *
 * `cascade` is required the moment the subtree is larger than the node itself.
 */
export async function documentDelete({ node_id: nodeId, scope, cascade }) {
  const wire = scope || PERSONAL;
  const refusal = await refuseUnwritableDocScope(wire);
  if (refusal) return refusal;

  const docScope = toDocScope(wire);
  const nodes = await listTree(docScope);
  const index = indexNodes(nodes);
  const subtree = collectSubtree(index, nodeId);
  if (!subtree.length) {
    return mcpError('not_found', 'No document or folder with that id in this scope.');
  }

  if (subtree.length > 1 && cascade !== true) {
    const descendants = subtree.slice(1);
    return mcpError(
      'cascade_required',
      'That node holds other items, and deleting it deletes all of them permanently — there is no undo. Nothing was deleted. Call again with cascade: true if that is what you want; the nodes listed here are exactly what would go.',
      {
        nodeId: subtree[0].id,
        count: subtree.length,
        documents: subtree.filter((n) => n.type === 'document').length,
        folders: subtree.filter((n) => n.type === 'folder').length,
        nodes: summarise(descendants),
        scope: wire,
        ...scopeReport(wire),
      }
    );
  }

  // Deepest-first: reversing a pre-order DFS puts every node after all of its
  // descendants. See `documentsTree.js`. `deleteNodes` marks the document ids
  // in `deletedDocsGuard` synchronously before its first write, and on a
  // mid-loop failure lifts the marks of the tail it never reached.
  const ordered = [...subtree].reverse();
  try {
    await deleteNodes(ordered, docScope);
  } catch {
    // Deliberately not `err.message` — a store error can carry a path or an id.
    return mcpError(
      'write_failed',
      'The delete stopped part-way. Some of those items are gone and the rest are still there; read the tree again before retrying.',
      { nodeId: subtree[0].id, complete: false, scope: wire }
    );
  }

  return {
    id: subtree[0].id,
    deleted: ordered.map((n) => n.id),
    count: ordered.length,
    complete: true,
    scope: wire,
    ...scopeReport(wire),
  };
}

// `document_move` lives in documentMove.js (card 4.4); re-exported so the
// handler map's import stays one line.
export { documentMove } from './documentMove';
